import {
  API,
  USER_CACHE_KEY
} from '@config/index';
import {
  FrameType,
  attributeToLocal,
  dataQueryToLocal,
  serviceInfoToLocal,
  originKeyToLocal,
  Attribute,
  DataQuery,
  ProviderType,
  externalTypeToLocal,
  ParameterConfig,
  serviceSlotToLocal
} from '@interface/index';
import { localizedString } from '@locale/locale';
import { defaultState } from './index';
import { Dispatch } from 'redux';
import { EffectDeclare } from '@lib/model-proxy';
import { message } from 'antd';
import { store } from '@reducer/index';
import { globalModel } from '@reducer/global';
import { ParameterMapStore } from '@lib/parameter-map';
import { dataQueryToParameter } from '@lib/quick-func';

export const effects: EffectDeclare = {};

effects.fetchService = ({ agentId, id }: { agentId?: string, id?: string }) => {
  return async (dispatch: Dispatch) => {

    const res: any = await API.service.getService(agentId, id);

    const userInfo = JSON.parse(
      window.localStorage.getItem(USER_CACHE_KEY) || '{}'
    );
    const readonly = res.org === 'naturali' && userInfo.org !== 'naturali';
    const selectedParametersPayload: Attribute[] = [];

    if (res.attributes && res.attributes.length) {
      const kvs: { [key: string]: ParameterConfig } = {};

      res.attributes.forEach((parameter: any) => {
        const target: Attribute | undefined = attributeToLocal(parameter);

        if (target) {
          selectedParametersPayload.push(target);
          kvs[target.id || ''] = target;
        }
      });

      ParameterMapStore.setMany(kvs);
    }

    // DataQueries
    const dataQueriesPayload: DataQuery[] = [];

    const kvs: { [key: string]: ParameterConfig } = {};

    (res.data_queries || []).forEach((item: any) => {
      const data: DataQuery = dataQueryToLocal(item);
      dataQueriesPayload.push(data);
      kvs[data.id || ''] = dataQueryToParameter(data);
    });

    ParameterMapStore.setMany(kvs);

    const payload = {
      ...defaultState,
      readonly,
      selectedParameters: selectedParametersPayload,
      nestedSlotCustomizations: res.nested_slot_customizations && res.nested_slot_customizations.map(attributeToLocal),
      id: res.id,
      scope: res.scope,
      name: res.name || '',
      comment: res.comment,
      source: res.source,
      originKey: res.origin_key && originKeyToLocal(res.origin_key),
      structKey: res.struct_key && originKeyToLocal(res.struct_key),
      parentId: res.parent_id || '',
      selectedCompositeInherit: (res.inherited_frames || []).map((item: any) => ({
        id: item.frame.id,
        name: item.frame.name,
        type: item.frame.type,
        state: item.state
      })),
      dataQueries: dataQueriesPayload,
      services: (res.services || []).map(serviceSlotToLocal),
      qualification: res.qualification,
      externalQualification: res.external_qualification,
      providerType: res.provider_type || ProviderType.Unknown,
      deployMode: res.deploy_mode,
      sqlConfig: res.sql_config && {
        url: res.sql_config.url,
        user: res.sql_config.user,
        password: res.sql_config.password,
        driver: res.sql_config.driver
      },
      webhookConfig: res.webhook_config && {
        url: res.webhook_config.url,
        pairs: res.webhook_config.pairs &&
          res.webhook_config.pairs.map((pair: any) => ({
            key: pair.key,
            value: pair.value
          })) || []
      },
      googleSheetsConfig: res.google_sheets_config && {
        spreadsheetId: res.google_sheets_config.spreadsheet_id,
        credential: res.google_sheets_config.credential
      },
      nativeConfig: res.native_config && {
        providerClassName: res.native_config.provider_class_name,
        configurationMeta: res.native_config.configuration_meta,
        dependency: res.native_config.dependency
      },
      externalTypes: (res.external_types || []).map(externalTypeToLocal),
      isInterface: res.is_interface,
      isImplementation: res.is_implementation,
      psql: res.psql
    };

    setTimeout(() => {
      const parsedService = serviceInfoToLocal(res);

      store.dispatch({
        payload: parsedService,
        type: 'global/service_update'
      });

      dispatch({
        payload,
        type: 'service/update_state'
      });

      window.sessionStorage.setItem('curFrame', JSON.stringify({
        id: payload.id,
        name: payload.name,
        frameType: FrameType.service,
        attributes: [
          ...payload.selectedParameters,
          ...payload.dataQueries
        ].map((attr: any) =>
          attr.id)
      }));
    }, 1000);

    return payload;
  };
};

effects.fetchParameters = ({ id, agentId }: { id: string, agentId?: string }) => {
  return async (dispatch: Dispatch) => {

    const { parameterPayload }: any = await dispatch<any>(globalModel.effects.fetchParameters({ id: agentId }));

    return parameterPayload;
  };
};

effects.patchService = (
  { id, data, agentId }: { id: string; data: any, agentId?: string },
  callback?: (res: any) => void
) => {
  return async (dispatch: Dispatch) => {
    const res: any = await API.service
      .updateService({ metaID: id, agentID: agentId }, data)
      .catch((err: any) => {
        message.warning(err.message || localizedString('common_save_failed'));
        throw new Error(err.message);
      });

    if (callback) {
      callback(res);
    }
  };
};
